import dayjs from "dayjs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  TableSortLabel,
} from "@mui/material";

/**
 * Formats US phone numbers to 999-999-9999.
 * Handles optional leading "1".
 */
const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const cleaned = String(phone).replace(/\D/g, "");

  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return String(phone);
};

const ProductionReportTable = ({
  visibleColumns,
  production_report_data,
  sortOrder,
  sortField,
  handleSort,
  isMobile,
}) => {
  /**
   * Common styles (DRY)
   */
  const headerCellSx = {
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    position: "sticky",
    top: 0,
    fontSize: isMobile ? "12px" : "14px",
    padding: isMobile ? "8px" : "12px",
  };

  const tableBaseSx = {
    minWidth: isMobile ? "100%" : "650px",
    "& .MuiTableCell-root": {
      fontSize: isMobile ? "12px" : "14px",
      padding: isMobile ? "8px" : "12px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: isMobile ? "100px" : "200px",
    },
  };

  /**
   * Column configuration (label, visibility, sortKey, cell renderer)
   * This removes repetitive JSX and keeps things consistent.
   */
  const columns = [
    {
      key: "customerName",
      label: "Customer Name",
      visible: visibleColumns.customerName,
      sortKey: "customerName",
      render: (row) => row.customerName,
    },
    {
      key: "policyNumber",
      label: "Policy Number",
      visible: visibleColumns.policyNumber,
      sortKey: "policyNumber",
      render: (row) => row.policyNumber,
    },
    {
      key: "agentName",
      label: "Agent",
      visible: visibleColumns.agentName,
      sortKey: "agentName",
      render: (row) => row.agentName,
    },
    {
      key: "location",
      label: "Location",
      visible: visibleColumns.location,
      // Note: original used sort field "office" for this column
      sortKey: "office",
      render: (row) => row.agencyName,
    },
    {
      key: "policyType",
      label: "Policy Type",
      visible: visibleColumns.policyType,
      sortKey: "policyType",
      render: (row) => row.policyType,
    },
    {
      key: "lineOfBusiness",
      label: "Line of Business",
      visible: visibleColumns.lineOfBusiness,
      sortKey: "lineOfBusiness",
      render: (row) => row.lineOfBusiness,
    },
    {
      key: "source",
      label: "Source",
      visible: visibleColumns.source,
      sortKey: "source",
      render: (row) => row.source,
    },
    {
      key: "carrier",
      label: "Carrier",
      visible: visibleColumns.carrier,
      // Note: original sorts on "company"
      sortKey: "company",
      render: (row) => row.company,
    },
    {
      key: "createdDate",
      label: "Created Date",
      visible: visibleColumns.createdDate,
      sortKey: "createdAt",
      render: (row) =>
        row.createdAt ? dayjs(row.createdAt).format("DD-MM-YYYY - hh:mm A") : "-",
    },
    {
      key: "effectiveDate",
      label: "Effective Date",
      visible: visibleColumns.effectiveDate,
      sortKey: "effectiveDate",
      render: (row) => row.effectiveDate,
    },
   {
      key: "expiryDate",
      label: "Expiration Date",
      visible: visibleColumns.expiryDate,
      sortKey: "expirationDate",
      render: (row) =>
        row.expirationDate ? dayjs(row.expirationDate).format("MM-DD-YYYY") : "-",
    },
    {
      key: "cancelDate",
      label: "Cancel Date",
      visible: visibleColumns.cancelDate,
      sortKey: "cancelDate",
      render: (row) => row.cancelDate,
    },
    {
      key: "emailId",
      label: "Email",
      visible: visibleColumns.emailId,
      sortKey: "emailId",
      render: (row) => row.emailId,
    },
    {
      key: "phoneNumber",
      label: "Phone Number",
      visible: visibleColumns.phoneNumber,
      sortKey: "phoneNumber",
      render: (row) => formatPhoneNumber(row.phoneNumber),
    },
    {
      key: "zipCode",
      label: "Zip Code",
      visible: visibleColumns.zipCode,
      sortKey: "zipCode",
      render: (row) => row.zipCode,
    },
    {
      key: "homeOwner",
      label: "Home Owner",
      visible: visibleColumns.homeOwner,
      sortKey: "homeOwner",
      render: (row) => (row.homeOwner ? "Yes" : "No"),
    },
    {
      key: "businessOwner",
      label: "Business Owner",
      visible: visibleColumns.businessOwner,
      sortKey: "businessOwner",
      render: (row) => (row.businessOwner ? "Yes" : "No"),
    },
    {
      key: "state",
      label: "State",
      visible: visibleColumns.state,
      sortKey: "state",
      render: (row) => row.state,
    },
    {
      key: "createdBy",
      label: "Created By",
      visible: visibleColumns.createdBy,
      sortKey: "createdBy",
      render: (row) => row.createdByName,
    },
    {
      key: "term",
      label: "Policy Terms",
      visible: visibleColumns.term,
      sortKey: "term",
      render: (row) => row.term,
    },
    {
      key: "status",
      label: "Status",
      visible: visibleColumns.status,
      sortKey: "status",
      render: (row) => row.status,
    },
    {
      key: "premium",
      label: "Premium",
      visible: visibleColumns.premium,
      sortKey: "premium",
      render: (row) => row.premium,
    },
  ];

  /**
   * Helper to render a sortable header cell with common styles.
   */
  const renderHeaderCell = (col) => {
    if (!col.visible) return null;
    return (
      <TableCell
        key={col.key}
        className="styled-table-heading"
        sx={headerCellSx}
        sortDirection={sortField === col.sortKey ? sortOrder : false}
      >
        <TableSortLabel
          active={sortField === col.sortKey}
          direction={sortField === col.sortKey ? sortOrder : "asc"}
          onClick={() => handleSort(col.sortKey)}
        >
          {col.label}
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: isMobile ? "60vh" : "none",
        overflow: "auto",
        width: "98%",
        marginBottom: "16px",
        marginLeft: "12px",
      }}
    >
      <Table stickyHeader sx={tableBaseSx} aria-label="policy table" size={isMobile ? "small" : "medium"}>
        {/* Header */}
        <TableHead sx={{ backgroundColor: "#F6C21E" }}>
          <TableRow sx={{ backgroundColor: "#F6C21E" }}>
            {columns.map(renderHeaderCell)}
          </TableRow>
        </TableHead>

        {/* Body */}
        <TableBody>
          {production_report_data.map((row, index) => (
            <TableRow className="styled-table-row" key={index}>
              {columns.map((col) =>
                col.visible ? <TableCell key={col.key}>{col.render(row)}</TableCell> : null
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProductionReportTable;
